import { useState, useEffect, memo } from "react";
import { getUserPhoto } from "../../services/commonService";
import Avatar from "../avatar";

const SESSION_KEY = "asyncAvatarCache_v1";
const avatarCache = new Map();

try {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) {
    const obj = JSON.parse(stored);
    Object.entries(obj).forEach(([k, v]) => {
      if (v) avatarCache.set(k, v);
    });
  }
} catch (e) {}

const saveSessionCache = () => {
  try {
    const obj = {};
    avatarCache.forEach((v, k) => {
      if (v) obj[k] = v;
    });
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(obj));
  } catch (e) {}
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    } catch (err) {
      reject(err);
    }
  });

const normalizeImageForCache = async (maybeUrl) => {
  try {
    if (!maybeUrl) return "";
    if (typeof maybeUrl === "string") {
      if (maybeUrl.startsWith("blob:")) {
        const resp = await fetch(maybeUrl);
        const blob = await resp.blob();
        return await blobToDataUrl(blob);
      }
      return maybeUrl;
    }
    if (maybeUrl instanceof Blob) {
      return await blobToDataUrl(maybeUrl);
    }
    return String(maybeUrl);
  } catch (err) {
    return "";
  }
};

const loadImage = (url, timeoutMs = 5000) =>
  new Promise((resolve) => {
    if (!url) return resolve(false);
    const img = new Image();
    let timedOut = false;
    const t = setTimeout(() => {
      timedOut = true;
      img.src = ""; // stop loading
      resolve(false);
    }, timeoutMs);
    img.onload = () => {
      if (!timedOut) {
        clearTimeout(t);
        resolve(true);
      }
    };
    img.onerror = () => {
      if (!timedOut) {
        clearTimeout(t);
        resolve(false);
      }
    };
    img.src = url;
  });

const AsyncAvatar = ({ id, name, size = "small" }) => {
  const [imgUrl, setImgUrl] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async (userId) => {
      // if cached in-memory, try to use it but verify it loads
      if (avatarCache.has(userId)) {
        const cached = avatarCache.get(userId) || "";
        /* istanbul ignore if -- defensive: the effect only invokes load() when the
           entry is missing or empty, so a truthy cached value here is unreachable in
           practice; the truthy re-verify path is exercised by the background verifier. */
        if (cached) {
          const ok = await loadImage(cached);
          if (ok) {
            if (!cancelled) setImgUrl(cached);
            return;
          }
          // cached image failed to load -> clear cache entry so we attempt fresh fetch
          avatarCache.set(userId, "");
          saveSessionCache();
        } else {
          if (!cancelled) setImgUrl("");
        }
      }

      try {
        const raw = await getUserPhoto(userId);
        const normalized = await normalizeImageForCache(raw);
        if (normalized) {
          const ok = await loadImage(normalized);
          if (ok) {
            avatarCache.set(userId, normalized);
            saveSessionCache();
            if (!cancelled) setImgUrl(normalized);
            return;
          }
        }
        // fallback: store empty and show initials
        avatarCache.set(userId, "");
        saveSessionCache();
        if (!cancelled) setImgUrl("");
      } catch (err) {
        avatarCache.set(userId, "");
        saveSessionCache();
        if (!cancelled) setImgUrl("");
      }
    };

    if (id) {
      setImgUrl(avatarCache.get(id) || "");
      if (!avatarCache.has(id) || !avatarCache.get(id)) load(id);
      else {
        // if cached but we set it optimistically, still verify in background and clear if invalid
        (async () => {
          const cached = avatarCache.get(id) || "";
          if (cached) {
            const ok = await loadImage(cached);
            if (!ok) {
              avatarCache.set(id, "");
              saveSessionCache();
              if (!cancelled) setImgUrl("");
            }
          }
        })();
      }
    } else {
      setImgUrl("");
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  return <Avatar userName={name} src={imgUrl} size={size} />;
};

export default memo(AsyncAvatar);